import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { importClients, getClientsList } from "../../../services/client";
import {
  ROW_STATUS,
  countRowsByStatus,
  getRowsToImport,
  mergeImportReport,
  parseClientImportFile,
  validateImportRows,
} from "./importClientUtils";

const STATUS_ROW_CLASS = {
  [ROW_STATUS.READY]: "",
  [ROW_STATUS.INVALID]: "bg-[#fff5f5]",
  [ROW_STATUS.SKIPPED]: "bg-[#fffbeb]",
  [ROW_STATUS.CREATED]: "bg-[#f0fdf4]",
  [ROW_STATUS.FAILED]: "bg-[#fff5f5]",
};

const STATUS_BADGE_CLASS = {
  [ROW_STATUS.READY]: "bg-[#e6f7f7] text-[#007a79]",
  [ROW_STATUS.INVALID]: "bg-[#fde8e8] text-[#9b1c1c]",
  [ROW_STATUS.SKIPPED]: "bg-[#fef3c7] text-[#92400e]",
  [ROW_STATUS.CREATED]: "bg-[#dcfce7] text-[#166534]",
  [ROW_STATUS.FAILED]: "bg-[#fde8e8] text-[#9b1c1c]",
};

const resolveIssueMessage = (issueCodes, t) => {
  if (!issueCodes?.length) return "";
  const messages = issueCodes.map((code) => {
    if (code === "missing_required") {
      return t("settingsClients.importErrorMissingRequired", {
        fields: t("settingsClients.fieldName"),
      });
    }
    if (code === "duplicate_name") {
      return t("settingsClients.importErrorDuplicateName");
    }
    if (code === "duplicate_reference") {
      return t("settingsClients.importErrorDuplicateReference");
    }
    return code;
  });
  return [...new Set(messages)].join(" · ");
};

const ImportSummaryChips = ({ counts, t }) => {
  const chips = [
    {
      key: "ready",
      count: counts[ROW_STATUS.READY] || 0,
      label: t("settingsClients.importStatusReady"),
      className: "bg-[#e6f7f7] text-[#007a79]",
    },
    {
      key: "skipped",
      count: counts[ROW_STATUS.SKIPPED] || 0,
      label: t("settingsClients.importStatusSkipped"),
      className: "bg-[#fef3c7] text-[#92400e]",
    },
    {
      key: "invalid",
      count: counts[ROW_STATUS.INVALID] || 0,
      label: t("settingsClients.importStatusInvalid"),
      className: "bg-[#fde8e8] text-[#9b1c1c]",
    },
    {
      key: "created",
      count: counts[ROW_STATUS.CREATED] || 0,
      label: t("settingsClients.importStatusCreated"),
      className: "bg-[#dcfce7] text-[#166534]",
    },
    {
      key: "failed",
      count: counts[ROW_STATUS.FAILED] || 0,
      label: t("settingsClients.importStatusFailed"),
      className: "bg-[#fde8e8] text-[#9b1c1c]",
    },
  ].filter((chip) => chip.count > 0);

  if (!chips.length) return null;

  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${chip.className}`}
        >
          {chip.count} {chip.label}
        </span>
      ))}
    </div>
  );
};

const ImportClientsModal = ({ isOpen, onClose, onImported }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [existingClients, setExistingClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const resetState = useCallback(() => {
    setFileName("");
    setRows([]);
    setImportDone(false);
    setImportReport(null);
  }, []);

  const handleClose = () => {
    if (isImporting) return;
    resetState();
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setIsLoadingClients(true);

    getClientsList()
      .then((response) => {
        if (!cancelled) {
          setExistingClients(response?.data?.data || []);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setExistingClients([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingClients(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const validatedRows = useMemo(() => {
    const validated = validateImportRows(rows, existingClients);
    if (!importReport) return validated;
    return mergeImportReport(validated, importReport);
  }, [rows, existingClients, importReport]);

  const statusCounts = useMemo(
    () => countRowsByStatus(validatedRows),
    [validatedRows],
  );

  const statusByLine = useMemo(() => {
    const map = new Map();
    validatedRows.forEach((row) => map.set(row.line, row));
    return map;
  }, [validatedRows]);

  const readyCount = statusCounts[ROW_STATUS.READY] || 0;

  const handleCellChange = (line, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.line === line ? { ...row, [field]: value } : row)),
    );
  };

  const getStatusLabel = (row) => {
    switch (row.status) {
      case ROW_STATUS.READY:
        return t("settingsClients.importStatusReady");
      case ROW_STATUS.INVALID:
        return t("settingsClients.importStatusInvalid");
      case ROW_STATUS.SKIPPED:
        return t("settingsClients.importStatusSkipped");
      case ROW_STATUS.CREATED:
        return t("settingsClients.importStatusCreated");
      case ROW_STATUS.FAILED:
        return t("settingsClients.importStatusFailed");
      default:
        return "";
    }
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const { rows: parsedRows } = await parseClientImportFile(file);
      setFileName(file.name);
      setRows(parsedRows);
      setImportDone(false);
      setImportReport(null);
    } catch (error) {
      console.error(error);
      toast.error(t("settingsClients.importInvalidTemplate"));
      resetState();
    }
  };

  const handleImport = async () => {
    const toImport = getRowsToImport(validatedRows);
    if (!toImport.length) {
      toast.error(t("settingsClients.importNoValidRows"));
      return;
    }

    setIsImporting(true);
    try {
      const response = await importClients(
        toImport.map(({ status, issueCodes, ...row }) => row),
      );
      const payload = response?.data || {};
      setImportReport(payload);
      setImportDone(true);

      const hasIssues =
        (payload.failedCount || 0) > 0 || (payload.skippedCount || 0) > 0;

      if (hasIssues) {
        toast.warning(
          t("settingsClients.importPartialSuccess", {
            created: payload.createdCount || 0,
            skipped: payload.skippedCount || 0,
            failed: payload.failedCount || 0,
          }),
        );
      } else {
        toast.success(
          t("settingsClients.importSuccess", {
            total: payload.createdCount || 0,
          }),
        );
      }

      if ((payload.createdCount || 0) > 0) {
        onImported?.();
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || t("settingsClients.importFailed"),
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      style={{ maxWidth: "800px" }}
      contentClassName="rounded-md"
    >
      <ModalHeader className="pb-0 !border-b-0 px-6 pt-6" toggle={handleClose}>
        <h4 className="font-semibold text-[34px] leading-[42px] m-0">
          {t("settingsClients.importModalTitle")}
        </h4>
      </ModalHeader>
      <ModalBody className="px-6 pt-4 pb-2">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleFileSelected}
        />

        {!fileName ? (
          <>
            <label className="block text-sm font-semibold text-[#222222] mb-1">
              {t("settingsClients.selectExcelFile")}
            </label>
            <p className="text-xs text-[#5e5e5e] mb-2 leading-[18px]">
              {t("settingsClients.importModalHelp")}
            </p>
            <p className="text-xs mb-3 leading-[18px]">
              <a
                href="/templates/Template_origines_dechets.xlsx"
                download="Template_origines_dechets.xlsx"
                className="text-[#00A7A6] font-semibold underline"
              >
                {t("settingsClients.downloadTemplate")}
              </a>
            </p>
          </>
        ) : null}

        <div className="flex items-center justify-between gap-2 mb-2 min-h-[40px]">
          {fileName ? (
            <p className="m-0 text-sm text-[#222222] truncate max-w-[480px]">
              {fileName}{" "}
              <span className="text-[#5e5e5e]">
                (
                {t("settingsClients.importPreviewCount", {
                  total: validatedRows.length,
                })}
                )
              </span>
            </p>
          ) : (
            <button
              type="button"
              className="modal-button modal-button-cancel"
              onClick={() => inputRef.current?.click()}
            >
              {t("Upload file")}
            </button>
          )}
        </div>

        {isLoadingClients && validatedRows.length > 0 ? (
          <p className="mb-3 text-xs text-[#5e5e5e]">
            {t("settingsClients.importCheckingDuplicates")}
          </p>
        ) : null}

        {validatedRows.length > 0 ? (
          <ImportSummaryChips counts={statusCounts} t={t} />
        ) : null}

        {validatedRows.length > 0 ? (
          <div className="border border-[#d3d3d3] rounded-lg p-3 bg-white">
            <div className="overflow-x-auto overflow-y-auto max-h-[280px]">
              <table className="w-full text-sm">
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "32%" }} />
                  <col style={{ width: "13%" }} />
                </colgroup>
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_#f0f0f0]">
                  <tr className="text-left text-[#222222]">
                    <th className="pb-2 pr-2 font-semibold bg-white">
                      {t("settingsClients.fieldName")}
                    </th>
                    <th className="pb-2 pr-2 font-semibold bg-white">
                      {t("settingsClients.fieldReference")}
                    </th>
                    <th className="pb-2 pr-2 font-semibold bg-white">
                      {t("settingsClients.fieldAddress")}
                    </th>
                    <th className="pb-2 font-semibold bg-white text-right">
                      {t("settingsClients.importColumnStatus")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((rawRow) => {
                    const row = statusByLine.get(rawRow.line) || rawRow;
                    const cellsDisabled = importDone || isImporting;
                    return (
                      <tr
                        key={rawRow.line}
                        className={`border-t border-[#f0f0f0] ${STATUS_ROW_CLASS[row.status] || ""}`}
                        title={resolveIssueMessage(row.issueCodes, t)}
                      >
                        <td className="py-1 pr-2 align-top">
                          <input
                            type="text"
                            value={rawRow.name ?? ""}
                            disabled={cellsDisabled}
                            placeholder={t("settingsClients.fieldName")}
                            onChange={(e) =>
                              handleCellChange(
                                rawRow.line,
                                "name",
                                e.target.value,
                              )
                            }
                            className={`w-full rounded border bg-transparent px-2 py-1 text-sm outline-none transition-colors focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent ${
                              !row.name
                                ? "border-[#f5c6c6] text-[#c53030]"
                                : "border-transparent text-[#222222] hover:border-[#e0e0e0]"
                            }`}
                          />
                        </td>
                        <td className="py-1 pr-2 align-top">
                          <input
                            type="text"
                            value={rawRow.clientUniqueId ?? ""}
                            disabled={cellsDisabled}
                            placeholder={t("settingsClients.fieldReference")}
                            onChange={(e) =>
                              handleCellChange(
                                rawRow.line,
                                "clientUniqueId",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm text-[#222222] outline-none transition-colors hover:border-[#e0e0e0] focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent"
                          />
                        </td>
                        <td className="py-1 pr-2 align-top">
                          <input
                            type="text"
                            value={rawRow.address ?? ""}
                            disabled={cellsDisabled}
                            placeholder={t("settingsClients.fieldAddress")}
                            onChange={(e) =>
                              handleCellChange(
                                rawRow.line,
                                "address",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm text-[#222222] outline-none transition-colors hover:border-[#e0e0e0] focus:border-[#2AB9B8] focus:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent"
                          />
                        </td>
                        <td className="py-1 text-right align-middle">
                          <span
                            className={`inline-block max-w-[100px] truncate rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4 ${STATUS_BADGE_CLASS[row.status] || ""}`}
                            title={resolveIssueMessage(row.issueCodes, t)}
                          >
                            {getStatusLabel(row)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {importDone ? (
          <p className="mt-3 mb-0 text-xs text-[#5e5e5e]">
            {t("settingsClients.importDoneHint")}
          </p>
        ) : null}
      </ModalBody>
      <ModalFooter className="!border-t-0 px-6 pb-6 pt-2">
        <button
          type="button"
          className="modal-button modal-button-cancel mr-2"
          onClick={handleClose}
          disabled={isImporting}
        >
          {importDone ? t("Close") : t("Cancel")}
        </button>
        {!importDone ? (
          <button
            type="button"
            className="modal-button modal-button-save"
            onClick={handleImport}
            disabled={isImporting || readyCount === 0 || isLoadingClients}
          >
            {readyCount > 0
              ? t("settingsClients.importActionButtonWithCount", {
                  total: readyCount,
                })
              : t("settingsClients.importActionButton")}
          </button>
        ) : (
          <button
            type="button"
            className="modal-button modal-button-save"
            onClick={() => {
              resetState();
              inputRef.current?.click();
            }}
          >
            {t("settingsClients.importAnotherFile")}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ImportClientsModal;
